import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import AuthCallback from './components/Auth/AuthCallback';
import AuthError from './components/Auth/AuthError';
import getConfig from './utils/configService';
import { lazyWithReload } from './utils/lazyWithReload';
import './styles/professional.css';

const JuWebSim = lazyWithReload(() => import('./JuWebSim'));
const DataPrivacyStatement = lazyWithReload(() => import('./DataPrivacyStatement'));
const Disclaimer = lazyWithReload(() => import('./Disclaimer'));
const WelcomePage = lazyWithReload(() => import('./WelcomePage'));
const LoginPage = lazyWithReload(() => import('./components/Auth/LoginPage'));
const HandoffEntry = lazyWithReload(() => import('./components/Handoff/HandoffEntry'));
const HandoffExpired = lazyWithReload(() => import('./components/Handoff/HandoffExpired'));

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: '#111113',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <div className="spinner"></div>
        <p style={{ color: '#cbd5e1', fontSize: '0.875rem' }}>Loading...</p>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// Routes component that has access to auth context
const SuspenseFallback = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    background: '#111113',
    flexDirection: 'column',
    gap: '16px'
  }}>
    <div className="spinner"></div>
    <p style={{ color: '#cbd5e1', fontSize: '0.875rem' }}>Loading...</p>
  </div>
);

function AppRoutes() {
  return (
    <Suspense fallback={<SuspenseFallback />}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<WelcomePage />} />
        <Route path="/privacy" element={<DataPrivacyStatement />} />
        <Route path="/disclaimer" element={<Disclaimer />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/success" element={<AuthCallback />} />
        <Route path="/auth/error" element={<AuthError />} />
        <Route path="/handoff/:token" element={<HandoffEntry />} />
        <Route path="/handoff-expired" element={<HandoffExpired />} />

        {/* Protected routes - require authentication */}
        <Route
          path="/draw"
          element={
            <ProtectedRoute>
              <JuWebSim />
            </ProtectedRoute>
          }
        />

        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  const config = getConfig();
  const maintenanceEnabled = config.MAINTENANCE_BANNER_ENABLED;
  const maintenanceText = config.MAINTENANCE_BANNER_TEXT || 'Scheduled maintenance in progress. Some actions may fail.';

  return (
    <>
      {maintenanceEnabled && (
        <div
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 2000,
            background: 'linear-gradient(90deg, #8b0000 0%, #b91c1c 100%)',
            color: '#fff',
            padding: '10px 16px',
            borderTop: '2px solid #fecaca',
            textAlign: 'center',
            fontWeight: 700,
            letterSpacing: '0.01em',
            pointerEvents: 'none',
          }}
          role="status"
          aria-live="polite"
        >
          {'⚠️ '}{maintenanceText}
        </div>
      )}
      <Router>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </Router>
    </>
  );
}

export default App;
